import { SimulationUsageInfoDto } from './hooks/useGetTokenUsageReport';

export const UNATTRIBUTED_USER_LABEL = 'Unattributed';

export type UserUsageRow = {
  email: string;
  totalUsage: number;
  simulationCount: number;
  projectCount: number;
  lastCompleted: number | null;
};

export const aggregateUsageByUser = (tokenUsage: SimulationUsageInfoDto[]): UserUsageRow[] => {
  const rows = new Map<string, UserUsageRow & { projects: Set<string> }>();

  for (const project of tokenUsage) {
    for (const simulation of project.simulationUsages ?? []) {
      const email = simulation.email || UNATTRIBUTED_USER_LABEL;
      const row = rows.get(email) ?? {
        email,
        totalUsage: 0,
        simulationCount: 0,
        projectCount: 0,
        lastCompleted: null,
        projects: new Set<string>(),
      };

      row.totalUsage += simulation.usage ?? 0;
      row.simulationCount += 1;
      row.projects.add(project.projectId);

      const completedAt = simulation.completedAt != null ? new Date(simulation.completedAt).getTime() : NaN;
      if (!Number.isNaN(completedAt) && (row.lastCompleted === null || completedAt > row.lastCompleted)) {
        row.lastCompleted = completedAt;
      }

      rows.set(email, row);
    }
  }

  return [...rows.values()]
    .map(({ projects, ...row }) => ({ ...row, projectCount: projects.size }))
    .sort((a, b) => b.totalUsage - a.totalUsage);
};

const escapeCsvValue = (value: string | number | null | undefined) => {
  const stringValue = value === null || value === undefined ? '' : `${value}`;
  // Prefix formula-like values so spreadsheets treat them as text.
  const safeValue =
    typeof value === 'string' && /^[\t\r\n ]*[=+\-@]/.test(stringValue) ? `'${stringValue}` : stringValue;
  return /[",\r\n]/.test(safeValue) ? `"${safeValue.replace(/"/g, '""')}"` : safeValue;
};

export const toCsv = (headers: string[], rows: (string | number | null | undefined)[][]) =>
  [headers, ...rows].map((row) => row.map(escapeCsvValue).join(',')).join('\n');

export const downloadCsv = (fileName: string, csv: string) => {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};
